import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

export type DraftItem = { id?: string; variant_id?: string | null; title: string; subtitle?: string | null; sku?: string | null; thumbnail?: string | null; quantity: number; unit_price: number; total?: number }
export type DraftAddress = { first_name?: string; last_name?: string; company?: string; address_1?: string; address_2?: string; city?: string; province?: string; postal_code?: string; country_code?: string; phone?: string }
export type Draft = {
  id: string
  draft_no: number | null
  email: string | null
  customer_id: string | null
  customer_name: string | null
  note: string
  items: DraftItem[]
  shipping: { name: string; amount: number; shipping_option_id: string } | null
  shipping_address: DraftAddress | null
  subtotal: number
  shipping_total: number
  tax_total: number
  total: number
  created_at: string
  updated_at: string
  invoice_sent_at: string | null
  invoice_to: string | null
}
export type DraftInput = {
  customer_id?: string | null
  email?: string | null
  items: ({ variant_id: string; quantity: number } | { title: string; unit_price: number; quantity: number })[]
  shipping_option_id?: string | null
  shipping_address?: DraftAddress | null
  note?: string | null
}

export const draftLabel = (d: Pick<Draft, "draft_no">) => (d.draft_no ? `#D${d.draft_no}` : "Draft")

export function useDraftList(p: { q?: string; limit: number; offset: number }) {
  return useQuery({
    queryKey: ["drafts", "list", p],
    placeholderData: keepPreviousData,
    queryFn: () => sdk.client.fetch<{ drafts: Draft[]; count: number }>("/admin/terify/draft-orders", { query: { q: p.q?.trim() || undefined, limit: p.limit, offset: p.offset } }),
  })
}

export function useDraft(id: string | undefined) {
  return useQuery({ queryKey: ["draft", id], enabled: !!id, queryFn: () => sdk.client.fetch<{ draft: Draft }>(`/admin/terify/draft-orders/${id}`) })
}

export function useSaveDraft(id: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: DraftInput) => sdk.client.fetch<{ draft: Draft }>(id ? `/admin/terify/draft-orders/${id}` : "/admin/terify/draft-orders", { method: "POST", body }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["drafts"] })
      qc.setQueryData(["draft", r.draft.id], r)
    },
  })
}

export function useDeleteDraft(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => sdk.client.fetch(`/admin/terify/draft-orders/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["drafts"] }),
  })
}

export function useCompleteDraft(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { paid: boolean; notify: boolean }) => sdk.client.fetch<{ order_id: string }>(`/admin/terify/draft-orders/${id}/complete`, { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["drafts"] })
      qc.invalidateQueries({ queryKey: ["orders"] })
    },
  })
}

/** Active products with variants and their base USD prices, for the "Browse products" drawer (Shopify hides drafts there too). */
/** The staff-entered USD price: no rules (region-scoped copies for tax-inclusive markets have one). */
export const basePrice = (prices: any[] | undefined) => (prices ?? []).find((x: any) => x.currency_code === "usd" && !x.price_list_id && !x.rules_count && !Object.keys(x.rules ?? {}).length)

export function useVariantSearch(q: string) {
  return useQuery({
    queryKey: ["draft-variants", q],
    queryFn: async () => {
      const { products } = await sdk.admin.product.list({ q: q || undefined, status: ["published"], limit: 30, fields: "id,title,thumbnail,status,*variants,*variants.prices" } as any)
      return (products as any[]).map((p) => ({
        id: p.id,
        title: p.title,
        thumbnail: p.thumbnail,
        status: p.status,
        variants: (p.variants ?? []).map((v: any) => ({
          id: v.id,
          title: v.title,
          sku: v.sku,
          price: Number(basePrice(v.prices)?.amount ?? (v.prices ?? [])[0]?.amount ?? 0),
        })),
      }))
    },
  })
}

export function useSendInvoice(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { to?: string; subject?: string; message?: string }) => sdk.client.fetch<{ sent: boolean; to: string; sent_at: string; invoice_url?: string }>(`/admin/terify/draft-orders/${id}/invoice`, { method: "POST", body: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["drafts"] }),
  })
}
